import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'

import type { ChannelPairing, OnboardingSession, ProvisioningStatus } from './types'

/**
 * `listAgents`/`runPlayground` vivem em `features/agents/api.ts` (fonte unica do contrato de
 * agentes). Reexportados aqui para a wizard (`useOnboarding`, `PlaygroundStep`) seguir importando de
 * `../api` sem duplicar a chamada.
 */
export { listAgents, runPlayground } from '@/features/agents/api'

/**
 * Ao contrario de `features/tenants/api.ts`, estes tres endpoints (WP-08a) ja tem `.Produces<T>()`
 * no OpenAPI, entao `result.data` ja vem tipado como `ProvisioningStatus`/`ChannelPairing` pelo
 * `openapi-fetch` — sem type guard `is*` escrito a mao. So resta o `undefined` defensivo (a uniao
 * de status codes do schema inclui respostas sem corpo tipado).
 */
function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

/** Inicia (ou, se ja existir, apenas devolve) o provisioning do tenant. Idempotente por tenant. */
export async function startProvisioning(tenantId: string): Promise<ProvisioningStatus> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/provisioning', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/** Status atual do provisioning, para o polling da wizard. */
export async function getProvisioningStatus(tenantId: string): Promise<ProvisioningStatus> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/provisioning', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/** Progresso atual do onboarding (retomavel). */
export async function getOnboarding(tenantId: string): Promise<OnboardingSession> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/onboarding', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/**
 * Persiste uma transicao do onboarding: avanca ate um estagio e/ou marca a primeira experiencia de
 * valor no Playground. Idempotente no backend — seguro re-chamar.
 */
export async function advanceOnboarding(
  tenantId: string,
  body: { stage?: string; firstValueReached?: boolean },
): Promise<OnboardingSession> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/onboarding/advance', {
    params: { path: { tenantId } },
    body,
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/** QR/pairing do canal. O QR nunca e logado nem persistido no cliente — so mantido em memoria. */
export async function getChannelPairing(
  tenantId: string,
  channelId: string,
): Promise<ChannelPairing> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/channels/{channelId}/pairing', {
    params: { path: { tenantId, channelId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}
